import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, GoldButton } from "@/components/shared";

const empty = {
  name: "", shore: "lincolnshire", category: "Food & Drink", short_pitch: "",
  origin_paragraph: "", description: "", image_url: "", website: "", town: "", plan: "harbour",
};

export default function EditListing() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [sectors, setSectors] = useState([]);
  const [existing, setExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/signin");
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectors(r.data));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/my/listing").then((r) => {
      if (r.data) {
        setExisting(true);
        setForm({ ...empty, ...r.data });
      } else if (user.plan) {
        setForm((f) => ({ ...f, plan: user.plan }));
      }
    }).finally(() => setLoading(false));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name, shore: form.shore, category: form.category,
        short_pitch: form.short_pitch, origin_paragraph: form.origin_paragraph,
        description: form.description, image_url: form.image_url, website: form.website,
        town: form.town, plan: form.plan,
      };
      if (existing) {
        await api.put("/my/listing", payload);
      } else {
        await api.post("/listings", payload);
      }
      toast.success("Submitted. Your listing is pending an admin's approval.");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!ready || !user || loading) {
    return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;
  }

  return (
    <section className="py-12 sm:py-16 bg-[#F8F1E7]">
      <Container>
        <button onClick={() => navigate("/account")} className="inline-flex items-center gap-1 text-[#505A68] hover:text-[#003366] text-sm mb-6 link-underline">
          <ArrowLeft size={14} /> Back to account
        </button>
        <div className="max-w-2xl">
          <h1 className="font-serif text-3xl text-[#003366] mb-2">{existing ? "Edit your listing" : "Submit a listing"}</h1>
          <p className="font-sans text-sm text-[#505A68] mb-8">
            All listings are reviewed before going live. Editing sends the listing back into the queue as pending.
          </p>

          <form onSubmit={submit} className="bg-[#FFFAF3] border border-[#D6D3CB] p-8 space-y-5" data-testid="listing-form">
            <div>
              <label className={labelCls}>Business name</label>
              <input required data-testid="listing-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Shore</label>
                <select data-testid="listing-shore" value={form.shore} onChange={(e) => set("shore", e.target.value)} className={inputCls}>
                  <option value="lincolnshire">Lincolnshire</option>
                  <option value="massachusetts">Massachusetts</option>
                  <option value="both">Both shores</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Sector</label>
                <select data-testid="listing-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                  {sectors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Town</label>
              <input data-testid="listing-town" value={form.town} onChange={(e) => set("town", e.target.value)} placeholder="e.g. Boston, Lincolnshire" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Short pitch (one line)</label>
              <input required maxLength={160} data-testid="listing-pitch" value={form.short_pitch} onChange={(e) => set("short_pitch", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Origin paragraph</label>
              <textarea rows={3} data-testid="listing-origin" value={form.origin_paragraph} onChange={(e) => set("origin_paragraph", e.target.value)} className={`${inputCls} resize-none`} placeholder="Where your business came from — this opens your listing." />
            </div>
            <div>
              <label className={labelCls}>Full description</label>
              <textarea rows={5} data-testid="listing-description" value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} resize-none`} />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Image URL</label>
                <input data-testid="listing-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Website</label>
                <input data-testid="listing-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Plan</label>
              <select data-testid="listing-plan" value={form.plan} onChange={(e) => set("plan", e.target.value)} className={inputCls}>
                <option value="harbour">Harbour — Free (company profile only)</option>
                <option value="stump">Stump — £5/mo (3 products)</option>
                <option value="beacon">Beacon — £12/mo (15 products, featured)</option>
                <option value="crossing">Crossing — £20/mo (unlimited, both shores)</option>
              </select>
            </div>
            <GoldButton type="submit" disabled={busy} data-testid="listing-submit" className="w-full">
              {busy ? <Loader2 size={16} className="animate-spin" /> : existing ? "Save & resubmit" : "Submit for approval"}
            </GoldButton>
          </form>
        </div>
      </Container>
    </section>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";
